import tags from 'lucide-static/tags.json';
import { listIconNames } from './resolve-icon-node';

const tagsByName = tags as Record<string, string[]>;

/**
 * Rank buckets, best first. Name matches beat tag matches because an author
 * typing "rocket" wants the icon called rocket, not everything tagged with it.
 * An exact tag beats a partial one so searching "link" is not buried under
 * everything tagged "linkedin".
 */
const NAME_PREFIX = 0;
const NAME_SUBSTRING = 1;
const TAG_EXACT = 2;
const TAG_PARTIAL = 3;

const rank = (name: string, query: string): number => {
  if (name.startsWith(query)) return NAME_PREFIX;
  if (name.includes(query)) return NAME_SUBSTRING;

  const tags = tagsByName[name];
  if (!tags) return -1;
  if (tags.some((tag) => tag === query)) return TAG_EXACT;
  if (tags.some((tag) => tag.includes(query))) return TAG_PARTIAL;
  return -1;
};

/**
 * Search icons by name and by Lucide's published tag index.
 *
 * Tag search is why the picker is worth changing: today's name-only list
 * cannot answer "launch" with `rocket`. An empty query returns every name so
 * the picker can show a browsable default.
 */
export const searchIconNames = (query: unknown, limit?: number): string[] => {
  const names = listIconNames();
  const normalized = typeof query === 'string' ? query.trim().toLowerCase() : '';

  if (!normalized) {
    return typeof limit === 'number' ? names.slice(0, limit) : names;
  }

  const matched = names
    .map((name) => ({ name, rank: rank(name, normalized) }))
    .filter((entry) => entry.rank >= 0)
    .sort((a, b) => a.rank - b.rank || a.name.localeCompare(b.name))
    .map((entry) => entry.name);

  return typeof limit === 'number' ? matched.slice(0, limit) : matched;
};

/** The tags Lucide publishes for an icon; empty when the name is unknown. */
export const getIconTags = (name: string): string[] => tagsByName[name] ?? [];
